// packages/api/src/PublicVendorService.ts
//
// ROADMAP #170 (a) — the VENDOR'S PAGE (`<site>/vendor/<token>`): the documents an event asks of a vendor, their uploads,
// and reuse of one already on file. Mirrors eventsmagic-api PublicVendorCore:
//   portal        — `publicVendorPortal`: what the page shows. A READ — nothing changes on opening the link.
//   uploadFile    — `publicVendorFileUpload`: one file (a PDF, JPEG or PNG), base64; kept pending until sent.
//   submitDocument — `publicVendorDocumentSubmit`: the document (files + what it is + dates), answering a request.
//   reuseDocument — `publicVendorUseDocument`: answer a request with a document already on file.
// Runs through the injected client (see `configureApiService`), so it works in the BROWSER (the proxy forwards the
// visitor's address — every action is rate-limited per visitor).
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import type { PublicEventOrganizer, PublicEventTheme } from "./PublicEventService";

export interface PublicVendorFile {
  id?: string | null;
  uploadedFileId?: string | null;
  uploadedFile?: { id?: string | null; fileUrl?: string | null; originalFilename?: string | null; mimeType?: string | null; sizeBytes?: number | null } | null;
}

export interface PublicVendorDocument {
  id: string;
  documentType: string;
  documentKind?: string | null;
  name: string;
  referenceNo?: string | null;
  /** YYYY-MM-DD. */
  issuedOn?: string | null;
  /** YYYY-MM-DD — the last day it is valid; null = does not expire. */
  expiresOn?: string | null;
  notes?: string | null;
  expiresBeforeEvent?: boolean | null;
  files?: PublicVendorFile[] | null;
}

export interface PublicVendorRequirement {
  id: string;
  documentType: string;
  documentKind?: string | null;
  name: string;
  instructions?: string | null;
  /** YYYY-MM-DD. */
  dueDate?: string | null;
  /** 'missing' | 'submitted' | 'approved' | 'returned'. */
  reviewStatus: string;
  /** Why it was sent back. */
  reviewNote?: string | null;
  /** 'overdue' | 'expires' | null. */
  attention?: string | null;
  document?: PublicVendorDocument | null;
}

export interface PublicVendorPortal {
  found: boolean;
  eventName?: string | null;
  eventStartsAt?: string | null;
  eventEndsAt?: string | null;
  /** The event's IANA zone — every date on the page is on its calendar. */
  eventTimezone?: string | null;
  vendorName?: string | null;
  serviceDescription?: string | null;
  theme?: PublicEventTheme | null;
  organizer?: Pick<PublicEventOrganizer, "id" | "slug" | "name" | "logoUploadedFile"> | null;
  requirements?: PublicVendorRequirement[] | null;
  /** Their documents still good for this event — reuse one instead of uploading again. */
  documentsOnFile?: PublicVendorDocument[] | null;
}

export interface PublicVendorDocumentInput {
  token: string;
  requirementId?: string | null;
  documentType: string;
  documentKind?: string | null;
  referenceNo?: string | null;
  issuedOn?: string | null;
  expiresOn?: string | null;
  notes?: string | null;
  fileIds: string[];
}

const DOCUMENT_FIELDS = `
  id
  documentType
  documentKind
  name
  referenceNo
  issuedOn
  expiresOn
  notes
  expiresBeforeEvent
  files { id uploadedFileId uploadedFile { id fileUrl originalFilename mimeType sizeBytes } }
`;

const PORTAL_FIELDS = `
  found
  eventName
  eventStartsAt
  eventEndsAt
  eventTimezone
  vendorName
  serviceDescription
  theme { id name mode tokens customCss }
  organizer { id slug name logoUploadedFile { fileUrl thumbnailUrl } }
  requirements { id documentType documentKind name instructions dueDate reviewStatus reviewNote attention document { ${DOCUMENT_FIELDS} } }
  documentsOnFile { ${DOCUMENT_FIELDS} }
`;

const PORTAL_QUERY = gql`
  query PublicVendorPortal($params: PublicVendorPortalInput) {
    publicVendorPortal(params: $params) {
      code
      errors { name errors }
      data { ${PORTAL_FIELDS} }
    }
  }
`;

const FILE_UPLOAD_MUTATION = gql`
  mutation PublicVendorFileUpload($params: PublicVendorFileUploadInput) {
    publicVendorFileUpload(params: $params) {
      code
      errors { name errors }
      data { uploadedFileId }
    }
  }
`;

const DOCUMENT_SUBMIT_MUTATION = gql`
  mutation PublicVendorDocumentSubmit($params: PublicVendorDocumentSubmitInput) {
    publicVendorDocumentSubmit(params: $params) {
      code
      errors { name errors }
      data { ${PORTAL_FIELDS} }
    }
  }
`;

const USE_DOCUMENT_MUTATION = gql`
  mutation PublicVendorUseDocument($params: PublicVendorUseDocumentInput) {
    publicVendorUseDocument(params: $params) {
      code
      errors { name errors }
      data { ${PORTAL_FIELDS} }
    }
  }
`;

export class PublicVendorService {
  /** What the vendor's page shows — a read; nothing changes on opening the link. */
  static async portal(token: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeQuery(PORTAL_QUERY, { params: { token } }, { fetchPolicy: "network-only", ...options });
  }

  /** One file, base64 (no `data:` prefix) — kept pending until a document is sent with it. */
  static async uploadFile(token: string, fileName: string, dataBase64: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(FILE_UPLOAD_MUTATION, { params: { token, fileName, dataBase64 } }, options);
  }

  /** The document — answers the request named, waiting for the organizer's review. Answers the page as it now is. */
  static async submitDocument(input: PublicVendorDocumentInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(DOCUMENT_SUBMIT_MUTATION, { params: input }, options);
  }

  /** Answer a request with a document already on file. Answers the page as it now is. */
  static async reuseDocument(token: string, requirementId: string, vendorDocumentId: string, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(USE_DOCUMENT_MUTATION, { params: { token, requirementId, vendorDocumentId } }, options);
  }
}
